import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Space,
  Tag,
  Typography,
  message,
  Modal,
  Row,
  Col,
  Upload,
  Popconfirm,
  Select,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';

const { Text } = Typography;
const { Option } = Select;

interface ActivityCategory {
  id: number;
  icon: string; // icon URL或icon名称
  name: {
    zh: string; // 简体中文
    zhTw: string; // 繁体中文
    en: string; // 英语
    vi: string; // 越南语
  };
  sortOrder: number;
  status: 'active' | 'inactive';
  createTime: string;
  updateTime: string;
}

// Demo数据
const mockCategories: ActivityCategory[] = [
  {
    id: 1,
    icon: 'user',
    name: {
      zh: '新人',
      zhTw: '新人',
      en: 'New User',
      vi: 'Người mới',
    },
    sortOrder: 1,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 2,
    icon: 'team',
    name: {
      zh: '代理',
      zhTw: '代理',
      en: 'Agent',
      vi: 'Đại lý',
    },
    sortOrder: 2,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 3,
    icon: 'video-camera',
    name: {
      zh: '真人',
      zhTw: '真人',
      en: 'Live Dealer',
      vi: 'Người thật',
    },
    sortOrder: 3,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 4,
    icon: 'play-circle',
    name: {
      zh: '直播间游戏',
      zhTw: '直播間遊戲',
      en: 'Live Room Game',
      vi: 'Trò chơi phòng trực tiếp',
    },
    sortOrder: 4,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 5,
    icon: 'trophy',
    name: {
      zh: '体育',
      zhTw: '體育',
      en: 'Sports',
      vi: 'Thể thao',
    },
    sortOrder: 5,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 6,
    icon: 'appstore',
    name: {
      zh: '全部',
      zhTw: '全部',
      en: 'All',
      vi: 'Tất cả',
    },
    sortOrder: 0,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
];

const ActivityCategoryManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState<ActivityCategory[]>(mockCategories);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ActivityCategory | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');

  // Icon选项（可以使用Ant Design Icons或其他图标库）
  const iconOptions = [
    { value: 'user', label: '用户' },
    { value: 'team', label: '团队' },
    { value: 'video-camera', label: '视频' },
    { value: 'play-circle', label: '播放' },
    { value: 'trophy', label: '奖杯' },
    { value: 'appstore', label: '应用' },
    { value: 'gift', label: '礼物' },
    { value: 'fire', label: '热门' },
    { value: 'star', label: '星标' },
    { value: 'heart', label: '心形' },
  ];

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      sortOrder: categories.length + 1,
    });
    setModalVisible(true);
  };

  const handleEdit = (category: ActivityCategory) => {
    setEditingCategory(category);
    form.setFieldsValue({
      ...category,
      icon: category.icon,
    });
    setModalVisible(true);
  };

  const handleDelete = (category: ActivityCategory) => {
    setCategories(prev => prev.filter(item => item.id !== category.id));
    message.success(`已删除活动大类「${category.name.zh}」`);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const now = new Date().toISOString().slice(0, 19).replace('T', ' ');

      if (editingCategory) {
        // 编辑
        setCategories(prev =>
          prev.map(item =>
            item.id === editingCategory.id
              ? {
                  ...item,
                  ...values,
                  updateTime: now,
                }
              : item
          )
        );
        message.success('活动大类已更新');
      } else {
        // 新增
        const newCategory: ActivityCategory = {
          id: Date.now(),
          ...values,
          createTime: now,
          updateTime: now,
        };
        setCategories(prev => [...prev, newCategory]);
        message.success('活动大类已创建');
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const filteredCategories = categories.filter(category => {
    if (!searchKeyword) return true;
    const keyword = searchKeyword.toLowerCase();
    return (
      category.name.zh.toLowerCase().includes(keyword) ||
      category.name.zhTw.toLowerCase().includes(keyword) ||
      category.name.en.toLowerCase().includes(keyword) ||
      category.name.vi.toLowerCase().includes(keyword)
    );
  });

  const columns = [
    {
      title: '序号',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      align: 'center' as const,
      sorter: (a: ActivityCategory, b: ActivityCategory) => a.sortOrder - b.sortOrder,
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      align: 'center' as const,
      render: (icon: string) => (
        <div style={{ fontSize: 24 }}>
          {icon === 'user' && '👤'}
          {icon === 'team' && '👥'}
          {icon === 'video-camera' && '📹'}
          {icon === 'play-circle' && '▶️'}
          {icon === 'trophy' && '🏆'}
          {icon === 'appstore' && '📱'}
          {icon === 'gift' && '🎁'}
          {icon === 'fire' && '🔥'}
          {icon === 'star' && '⭐'}
          {icon === 'heart' && '❤️'}
          {!['user', 'team', 'video-camera', 'play-circle', 'trophy', 'appstore', 'gift', 'fire', 'star', 'heart'].includes(icon) && '📦'}
        </div>
      ),
    },
    {
      title: '简体中文',
      dataIndex: ['name', 'zh'],
      key: 'nameZh',
      width: 120,
    },
    {
      title: '繁体中文',
      dataIndex: ['name', 'zhTw'],
      key: 'nameZhTw',
      width: 120,
    },
    {
      title: '英语',
      dataIndex: ['name', 'en'],
      key: 'nameEn',
      width: 150,
    },
    {
      title: '越南语',
      dataIndex: ['name', 'vi'],
      key: 'nameVi',
      width: 150,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 170,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 140,
      render: (_: any, record: ActivityCategory) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除该活动大类？"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const uploadProps: UploadProps = {
    name: 'icon',
    listType: 'picture-card',
    maxCount: 1,
    beforeUpload: () => false, // 阻止自动上传
    onChange: (info) => {
      if (info.fileList.length > 0) {
        const file = info.fileList[0].originFileObj;
        if (file) {
          // 这里可以处理文件上传，暂时只保存文件名
          form.setFieldValue('icon', file.name);
        }
      }
    },
  };

  return (
    <div style={{ padding: 24 }}>
      <Space align="center" style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          活动大类配置
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => message.info('已刷新')}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增活动大类
          </Button>
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="搜索活动大类（支持多语言）"
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            allowClear
          />
        </Space>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          dataSource={filteredCategories}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false, showTotal: total => `共 ${total} 条` }}
          scroll={{ x: 1200 }}
          bordered={false}
        />
      </Card>

      <Modal
        title={editingCategory ? '编辑活动大类' : '新增活动大类'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 'active',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Icon"
                name="icon"
                rules={[{ required: true, message: '请选择或上传Icon' }]}
              >
                <Input placeholder="输入icon名称（如：user, team等）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="排序"
                name="sortOrder"
                rules={[{ required: true, message: '请输入排序号' }]}
              >
                <Input type="number" placeholder="数字越小越靠前" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="简体中文名称"
                name={['name', 'zh']}
                rules={[{ required: true, message: '请输入简体中文名称' }]}
              >
                <Input placeholder="请输入简体中文名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="繁体中文名称"
                name={['name', 'zhTw']}
                rules={[{ required: true, message: '请输入繁体中文名称' }]}
              >
                <Input placeholder="请输入繁体中文名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="英语名称"
                name={['name', 'en']}
                rules={[{ required: true, message: '请输入英语名称' }]}
              >
                <Input placeholder="请输入英语名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="越南语名称"
                name={['name', 'vi']}
                rules={[{ required: true, message: '请输入越南语名称' }]}
              >
                <Input placeholder="请输入越南语名称" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="状态"
            name="status"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="active">启用</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ActivityCategoryManagement;

